import { cdnCommonPath } from "@/constants/cdnUrls";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useHeaderContext } from "@/contexts/HeaderContext";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { Link, usePathname } from "@/i18n/navigation";
import { getRouteDetails } from "@/utils/routeHelper";
import { triggerPriceAlarmToggleFromHeader } from "@/utils/priceAlarmToggleBridge";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useLocale } from "next-intl";
import Image from "next/image";
import { memo } from "react";
import HeaderPointConvertButton from "./HeaderPointConvertButton";
import UserButton from "./UserButton";
import Spinner from "./ui/Spinner";

const LanguageCurrencyButton = memo(function LanguageCurrencyButton() {
  const { showLanguageAndCurrencyModal } = useHeaderContext();
  const { loading } = useAuth();
  const { currency } = useCurrency();
  const locale = useLocale();
  const pathname = usePathname();
  const routeDetails = getRouteDetails(pathname);
  const { isPayment } = routeDetails;

  return (
    <button
      onClick={showLanguageAndCurrencyModal}
      className={clsx(
        "hidden w-[70px] cursor-pointer items-center justify-center gap-2",
        isPayment ? "hidden" : "md:inline-flex",
      )}
    >
      <Image
        src={cdnCommonPath + `/${locale}.svg`}
        alt={locale}
        width={24}
        height={24}
        priority
        quality={100}
      />

      <div className="bg-primary-weak w-[1px] self-stretch"></div>
      <span className="flex w-[30px] items-center justify-center text-center">
        {loading ? <Spinner className="h-4 w-4 animate-spin" /> : currency}
      </span>
    </button>
  );
});

const HeaderActions = memo(function HeaderActions({ className = "" }) {
  const { showReservationControl } = useHeaderContext();
  const t = useTranslationDetector();
  const tFlight = useTranslationDetector("flight");
  const pathname = usePathname();
  const routeDetails = getRouteDetails(pathname);
  const { isFlightResultPage, isReservation, isPayment, shouldBlurHeader } =
    routeDetails;
  return (
    <div
      className={clsx("ml-auto flex items-center justify-end gap-5", className)}
    >
      {!isReservation && !isPayment && (
        <>
          <button
            onClick={showReservationControl}
            className="hidden cursor-pointer whitespace-nowrap md:block"
          >
            {t("common.reservationcontrol")}
          </button>
          <Link href="/contact" className="hidden md:block">
            {t("common.help")}
          </Link>
        </>
      )}
      <LanguageCurrencyButton />
      {!isPayment && (
        <div className={shouldBlurHeader ? "" : "hidden md:block"}>
          <HeaderPointConvertButton />
        </div>
      )}
      {isFlightResultPage && (
        <button
          type="button"
          onClick={triggerPriceAlarmToggleFromHeader}
          aria-label={tFlight("priceAlarmCreate")}
          className="h-6 md:hidden"
        >
          <FontAwesomeIcon
            icon={byPrefixAndName.fak["bell-new"]}
            className="text-[23px] text-black"
          />
        </button>
      )}
      <div className="hidden md:block">
        <UserButton />
      </div>
    </div>
  );
});

export default HeaderActions;
